import { Component } from 'react';

import { usePrismicStore } from '@/zustandStore.js';

/**
 * Top-level error boundary. Catches render/commit-phase errors anywhere below it so a single
 * failure — e.g. an under-provisioned customer/hub — degrades to a recoverable message instead
 * of white-screening the entire admin app.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // Name the current customer so a residual crash points at the likely culprit (the
        // customer/Prismic config being rendered) instead of just a minified stack.
        const { currentCustomer } = usePrismicStore.getState();
        const customerLabel =
            currentCustomer?.key?.[0]?.text ??
            currentCustomer?.customer_name?.[0]?.text ??
            currentCustomer?.id ??
            'unknown';
        // eslint-disable-next-line no-console
        console.error(
            `[ErrorBoundary] Uncaught error (current customer: "${customerLabel}"):`,
            error,
            info?.componentStack
        );
    }

    render() {
        const { hasError } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        gap: '1rem',
                        padding: '2rem',
                        textAlign: 'center',
                        fontFamily: 'sans-serif',
                    }}
                >
                    <h2 style={{ margin: 0 }}>Something went wrong</h2>
                    <p style={{ maxWidth: 480, color: '#555' }}>
                        The page could not be displayed. This can happen when the selected customer is not fully
                        configured. Please reload, and contact support if the problem persists.
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{ padding: '0.5rem 1.25rem', cursor: 'pointer' }}
                    >
                        Reload
                    </button>
                </div>
            );
        }

        return children;
    }
}

export default ErrorBoundary;
